/**
 * WebMCP: exposes fmr8 tools to in-browser AI agents via the W3C Web Machine
 * Learning CG draft API (document.modelContext.registerTool). Chromium 146
 * shipped the earlier navigator.modelContext surface behind a flag, so both are
 * feature-detected. Tools call our own /api endpoints with the visitor's
 * session cookie, so account/Pro gating applies exactly as it does in the UI.
 */
import { authFetch } from './context/AuthContext';

type ToolResult = { content: Array<{ type: 'text'; text: string }> };
type Tool = {
  name: string;
  title?: string;
  description: string;
  inputSchema?: object;
  annotations?: { readOnlyHint?: boolean; consequentialHint?: boolean; untrustedContentHint?: boolean };
  execute: (input: any) => Promise<ToolResult>;
};

const ORIGIN = 'https://fmr8.com';
const MAX_TEXT = 20000;

const text = (t: string): ToolResult => ({ content: [{ type: 'text', text: t.slice(0, MAX_TEXT) }] });
const json = (v: unknown) => text(JSON.stringify(v, null, 1));

class TierError extends Error {}

async function call(path: string, init?: RequestInit): Promise<any> {
  const res = init ? await authFetch(path, init) : await fetch(path, { credentials: 'same-origin' });
  if (res.status === 401) {
    throw new TierError(`This needs a free fmr8 account. Ask the user to sign up or sign in at ${ORIGIN}/login, then retry.`);
  }
  if (res.status === 402) {
    throw new TierError(`This is an fmr8 Pro feature ($29/mo or $290/yr). The user can upgrade at ${ORIGIN}/pricing.`);
  }
  if (res.status === 403) throw new TierError('The request was refused (missing or invalid security token). Reload the page and retry.');
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || `Request failed (HTTP ${res.status})`);
  return body;
}

const qs = (params: Record<string, unknown>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, String(v)); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

const wrap = (fn: (input: any) => Promise<ToolResult>) => async (input: any): Promise<ToolResult> => {
  try {
    return await fn(input || {});
  } catch (e) {
    return text(e instanceof Error ? e.message : String(e));
  }
};

const zip = { type: 'string', pattern: '^\\d{5}$', description: '5-digit US ZIP code, e.g. "14207"' };
const state = { type: 'string', pattern: '^[A-Za-z]{2}$', description: '2-letter state abbreviation, e.g. "NY"' };
const year = { type: 'integer', minimum: 2018, maximum: 2030, description: 'HUD fiscal year, e.g. 2027' };
const ro = { readOnlyHint: true };

function gatedNote(d: any) {
  return Array.isArray(d?.gated) && d.gated.length
    ? `Hidden without a free account: ${d.gated.join(', ')}. Sign up at ${ORIGIN}/login to see them.`
    : undefined;
}

function pageContext() {
  const path = window.location.pathname;
  const m = (re: RegExp) => path.match(re);
  const zipM = m(/^\/zip(?:code)?\/(\d{5})/);
  const cityM = m(/^\/city\/([^/]+)\/([A-Za-z]{2})/);
  const stateM = m(/^\/state\/([A-Za-z]{2})/);
  const countyM = m(/^\/county\/([^/]+)\/([A-Za-z]{2})/);
  const haM = m(/^\/housing-authority\/([A-Za-z]{2})\/([a-z0-9-]+)/);
  return {
    url: window.location.href,
    title: document.title,
    page: zipM ? 'zip' : cityM ? 'city' : stateM ? 'state' : countyM ? 'county' : haM ? 'housing_authority'
      : path.startsWith('/map') ? 'map' : path === '/' ? 'home' : 'other',
    zip: zipM?.[1],
    city: cityM ? decodeURIComponent(cityM[1]) : undefined,
    county: countyM ? decodeURIComponent(countyM[1]) : undefined,
    state: (cityM?.[2] || stateM?.[1] || countyM?.[2] || haM?.[1])?.toUpperCase(),
    housingAuthoritySlug: haM?.[2],
  };
}

/** Tools default to the ZIP the visitor is looking at ("what's the 3BR rent here?"). */
function zipFrom(input: any): string {
  const z = input?.zip_code || pageContext().zip;
  if (!z || !/^\d{5}$/.test(z)) {
    throw new Error('No ZIP given and the current page is not a ZIP page. Pass zip_code, or use fmr_autocomplete to find one.');
  }
  return z;
}

const BR_KEYS = ['studio', 'oneBedroom', 'twoBedroom', 'threeBedroom', 'fourBedroom', 'fiveBedroom', 'sixBedroom', 'sevenBedroom', 'eightBedroom'];
const zipOpt = { ...zip, description: '5-digit ZIP code; defaults to the ZIP page the user is viewing' };

const TOOLS: Tool[] = [
  {
    name: 'fmr_current_page',
    title: 'Current fmr8 page',
    description: 'Describe the fmr8.com page the user is viewing (ZIP, city, state, county or housing authority), so follow-up tools can use it.',
    inputSchema: { type: 'object', properties: {} },
    annotations: ro,
    execute: wrap(async () => json(pageContext())),
  },
  {
    name: 'fmr_payment_standard',
    title: 'Section 8 rent for a unit size',
    description: 'How much Section 8 pays for an N-bedroom unit in a ZIP: HUD Fair Market Rent plus the 90%-110% payment standard range housing authorities set from it. Defaults to the ZIP page the user is viewing. Answers "what does Section 8 pay for a 3 bedroom here?"',
    inputSchema: { type: 'object', properties: { bedrooms: { type: 'integer', minimum: 0, maximum: 8, description: '0 = studio' }, zip_code: zipOpt }, required: ['bedrooms'] },
    annotations: ro,
    execute: wrap(async ({ bedrooms, zip_code }) => {
      const z = zipFrom({ zip_code });
      const d = await call(`/api/fmr/lookup/${z}`);
      const row = d.results?.[0];
      const fmr = row?.bedroomSizes?.[BR_KEYS[bedrooms]];
      if (!fmr) return text(`No FMR found for a ${bedrooms}-bedroom unit in ZIP ${z}.`);
      return json({
        zip: z,
        city: d.city,
        fiscalYear: row.year,
        bedrooms,
        fairMarketRent: fmr,
        paymentStandardRange: { at90pct: Math.round(fmr * 0.9), at100pct: fmr, at110pct: Math.round(fmr * 1.1) },
        fmrScope: d.fmrScope,
        caveat: 'Each housing authority sets its own payment standard within 90-110% of FMR (exceptions can go higher); gross rent must also cover the tenant utility allowance.',
        housingAuthorities: (d.housingAuthorities || []).slice(0, 3).map((h: any) => h.name),
        page: `${ORIGIN}/zipcode/${z}`,
      });
    }),
  },
  {
    name: 'fmr_lookup_zip',
    title: 'FMR lookup by ZIP code',
    description: 'HUD Section 8 Fair Market Rent (Small Area FMR where HUD publishes it) for a ZIP code, studio through 8 bedrooms. Omit year for all available years, newest first.',
    inputSchema: { type: 'object', properties: { zip_code: zipOpt, year } },
    annotations: ro,
    execute: wrap(async (input) => {
      const z = zipFrom(input);
      const d = await call(`/api/fmr/lookup/${z}${qs({ year: input.year })}`);
      return json({ zip: z, city: d.city, fmrScope: d.fmrScope, results: d.results, note: gatedNote(d), page: `${ORIGIN}/zipcode/${z}` });
    }),
  },
  {
    name: 'fmr_search',
    title: 'Search FMR by state/county',
    description: 'Search Fair Market Rent records by state and/or county name. Returns matching ZIP codes with their FMR by bedroom count.',
    inputSchema: {
      type: 'object',
      properties: { state, county: { type: 'string', description: 'County name (partial match)' }, year, limit: { type: 'integer', minimum: 1, maximum: 100, default: 20 } },
    },
    annotations: ro,
    execute: wrap(async ({ state: s, county, year: y, limit = 20 }) =>
      json(await call(`/api/fmr/search${qs({ state: s?.toUpperCase(), county, year: y, limit })}`))),
  },
  {
    name: 'fmr_list_states',
    title: 'List states',
    description: 'List the US states and territories fmr8 has Fair Market Rent data for.',
    inputSchema: { type: 'object', properties: {} },
    annotations: ro,
    execute: wrap(async () => json(await call('/api/fmr/states'))),
  },
  {
    name: 'fmr_get_state',
    title: 'State FMR overview',
    description: 'Average Fair Market Rent for a state, with its counties and top cities.',
    inputSchema: { type: 'object', properties: { state }, required: ['state'] },
    annotations: ro,
    execute: wrap(async ({ state: s }) => json(await call(`/api/fmr/state/${String(s).toUpperCase()}`))),
  },
  {
    name: 'fmr_get_county',
    title: 'County FMR data',
    description: 'Fair Market Rent for every ZIP code in a county.',
    inputSchema: { type: 'object', properties: { county: { type: 'string', description: 'County name, e.g. "Erie County"' }, state }, required: ['county', 'state'] },
    annotations: ro,
    execute: wrap(async ({ county, state: s }) =>
      json(await call(`/api/fmr/county/${encodeURIComponent(county)}/${String(s).toUpperCase()}`))),
  },
  {
    name: 'fmr_get_city',
    title: 'City FMR data',
    description: "A city's ZIP codes and their Section 8 Fair Market Rents.",
    inputSchema: { type: 'object', properties: { city: { type: 'string', description: 'City name, e.g. "Buffalo"' }, state, year }, required: ['city', 'state'] },
    annotations: ro,
    execute: wrap(async ({ city, state: s, year: y }) =>
      json(await call(`/api/fmr/city/${encodeURIComponent(city)}/${String(s).toUpperCase()}${qs({ year: y })}`))),
  },
  {
    name: 'fmr_autocomplete',
    title: 'Find a place',
    description: 'Autocomplete a ZIP code, city, county or state name to the exact identifiers the other fmr8 tools expect.',
    inputSchema: { type: 'object', properties: { query: { type: 'string', minLength: 2 }, type: { type: 'string', enum: ['zip', 'city', 'county', 'state'] } }, required: ['query'] },
    annotations: ro,
    execute: wrap(async ({ query, type }) => json(await call(`/api/fmr/autocomplete${qs({ q: query, type })}`))),
  },
  {
    name: 'fmr_historical_trends',
    title: 'FMR history for a ZIP',
    description: 'Year-over-year Fair Market Rent history for a ZIP code, to show how Section 8 rent limits have changed.',
    inputSchema: { type: 'object', properties: { zip_code: zipOpt } },
    annotations: ro,
    execute: wrap(async (input) => {
      const zip_code = zipFrom(input);
      const d = await call(`/api/fmr/lookup/${zip_code}`);
      return json({ zip: zip_code, byYear: (d.results || []).map((r: any) => ({ year: r.year, bedroomSizes: r.bedroomSizes })) });
    }),
  },
  {
    name: 'fmr_zip_stats',
    title: 'ZIP area statistics',
    description: 'Census demographics, utility allowances, home values, Zillow market rent and Redfin sale-market stats for a ZIP. Market data needs a free fmr8 account.',
    inputSchema: { type: 'object', properties: { zip_code: zipOpt } },
    annotations: ro,
    execute: wrap(async (input) => {
      const zip_code = zipFrom(input);
      const d = await call(`/api/fmr/lookup/${zip_code}`);
      return json({
        zip: zip_code,
        censusDemographics: d.censusDemographics,
        utilityAllowances: d.utilityAllowances,
        zillowPricing: d.zillowPricing,
        zillowMarketRent: d.zillowMarketRent,
        redfinMarketStats: d.redfinMarketStats,
        note: gatedNote(d),
      });
    }),
  },
  {
    name: 'fmr_housing_authorities_for_zip',
    title: 'Housing authorities serving a ZIP',
    description: 'Public housing authorities that administer Section 8 vouchers in a ZIP code, largest first, with voucher counts and their fmr8 pages.',
    inputSchema: { type: 'object', properties: { zip_code: zipOpt } },
    annotations: ro,
    execute: wrap(async (input) => {
      const d = await call(`/api/fmr/lookup/${zipFrom(input)}`);
      return json((d.housingAuthorities || []).map((h: any) => ({
        ...h,
        page: `${ORIGIN}/housing-authority/${h.state}/${h.slug}`,
      })));
    }),
  },
  {
    name: 'fmr_map_geography_stats',
    title: 'Map statistics for an area',
    description: 'Statistics shown on the fmr8 map for one area: state (2-digit FIPS), county (5-digit FIPS), zcta (ZIP), tract (11-digit) or block group "bg" (12-digit). Includes FMR, ACS census figures and voucher counts. Tract and block-group level need a free account.',
    inputSchema: {
      type: 'object',
      properties: {
        level: { type: 'string', enum: ['state', 'county', 'zcta', 'tract', 'bg'] },
        geoid: { type: 'string', pattern: '^\\d{2,12}$', description: 'Census GEOID; for level "zcta" this is the ZIP code' },
      },
      required: ['level', 'geoid'],
    },
    annotations: ro,
    execute: wrap(async ({ level, geoid }) => json(await call(`/api/map/feature/${level}/${geoid}`))),
  },
  {
    name: 'fmr_deal_feed',
    title: 'Section 8 deal feed (Pro)',
    description: 'Show Section 8 deals here: for-sale listings scored for Section 8 fit (FMR vs price, cash-on-cash, grade A+ to F), linking to the source listing. fmr8 Pro only.',
    inputSchema: {
      type: 'object',
      properties: {
        zip_code: { ...zip, description: 'Deals in this ZIP. Defaults to the ZIP page the user is viewing; omit on other pages to search by state.' },
        state,
        min_grade: { type: 'string', enum: ['A+', 'A', 'B', 'C', 'D', 'F'], default: 'B' },
        limit: { type: 'integer', minimum: 1, maximum: 100, default: 25 },
      },
    },
    annotations: { readOnlyHint: true, untrustedContentHint: true },
    execute: wrap(async ({ zip_code, state: s, min_grade = 'B', limit = 25 }) => {
      const z = zip_code || (!s ? pageContext().zip : undefined);
      if (z) {
        const d = await call(`/api/deals/zip/${z}${qs({ minGrade: min_grade })}`);
        return json({ ...d, data: (d.data || []).slice(0, limit) });
      }
      return json(await call(`/api/deals/feed${qs({ state: s?.toUpperCase(), minGrade: min_grade, limit })}`));
    }),
  },
  {
    name: 'fmr_list_glossary',
    title: 'Section 8 glossary',
    description: 'List Section 8 and HUD glossary terms with short definitions.',
    inputSchema: { type: 'object', properties: {} },
    annotations: ro,
    execute: wrap(async () => json(await call('/api/fmr/glossary'))),
  },
  {
    name: 'fmr_get_glossary_term',
    title: 'Glossary term',
    description: 'Full definition and FAQs for a glossary term, by slug (e.g. "payment-standard", "safmr", "utility-allowance").',
    inputSchema: { type: 'object', properties: { slug: { type: 'string', pattern: '^[a-z0-9-]+$' } }, required: ['slug'] },
    annotations: ro,
    execute: wrap(async ({ slug }) => json(await call(`/api/fmr/glossary/${slug}`))),
  },
  {
    name: 'fmr_follow_housing_authority',
    title: 'Follow a housing authority',
    description: "Follow a housing authority to get email alerts about waitlist openings and payment standard changes. Changes the user's account and asks them to confirm first. Get pha_code from fmr_housing_authorities_for_zip.",
    inputSchema: {
      type: 'object',
      properties: {
        pha_code: { type: 'string', pattern: '^[A-Za-z]{2}\\d{3}$', description: 'HUD PHA code, e.g. "NY002"' },
        frequency: { type: 'string', enum: ['instant', 'weekly'], default: 'instant' },
      },
      required: ['pha_code'],
    },
    annotations: { consequentialHint: true },
    execute: wrap(async ({ pha_code, frequency = 'instant' }) => {
      const code = String(pha_code).toUpperCase();
      // The draft spec has no user-interaction hook yet, so ask in the page itself.
      if (!window.confirm(`An AI assistant wants to follow housing authority ${code} for ${frequency} email alerts on your fmr8 account. Allow?`)) {
        return text('The user declined to follow this housing authority.');
      }
      const d = await call(`/api/pha/${code}/subscribe`, { method: 'POST', body: JSON.stringify({ frequency }) });
      return json(d);
    }),
  },
];

let registered = false;

export function registerWebMcpTools(): void {
  if (registered || typeof window === 'undefined') return;
  const ctx: any = (document as any).modelContext ?? (navigator as any).modelContext;
  if (!ctx || typeof ctx.registerTool !== 'function') return;
  registered = true;
  const controller = new AbortController();
  window.addEventListener('pagehide', () => controller.abort(), { once: true });
  for (const tool of TOOLS) {
    try {
      Promise.resolve(ctx.registerTool(tool, { signal: controller.signal })).catch((e: unknown) =>
        console.warn(`WebMCP: could not register ${tool.name}`, e));
    } catch (e) {
      console.warn(`WebMCP: could not register ${tool.name}`, e);
    }
  }
}

export const WEBMCP_TOOL_NAMES = TOOLS.map((t) => t.name);
